import { useMemo, useState } from "react";

import type {
  TokenUsageBucket,
  TokenUsageBucketSize,
  TokenUsageResponse,
} from "@oracus/shared/api/token-usage";

import { useTokenUsage } from "../api/token-usage.api.ts";
import { Card, Page } from "../components/Page.tsx";

type RangeKey = "24h" | "7d" | "30d" | "90d";

interface RangeConfig {
  label: string;
  hours: number;
  bucket: TokenUsageBucketSize;
}

const RANGES: Record<RangeKey, RangeConfig> = {
  "24h": { label: "Last 24 hours", hours: 24, bucket: "hour" },
  "7d": { label: "Last 7 days", hours: 24 * 7, bucket: "day" },
  "30d": { label: "Last 30 days", hours: 24 * 30, bucket: "day" },
  "90d": { label: "Last 90 days", hours: 24 * 90, bucket: "week" },
};

export function TokenUsage() {
  const [range, setRange] = useState<RangeKey>("30d");
  const { from, to, bucket } = useMemo(() => {
    const cfg = RANGES[range];
    const now = new Date();
    const fromDt = new Date(now.getTime() - cfg.hours * 60 * 60 * 1000);
    return {
      from: fromDt.toISOString(),
      to: now.toISOString(),
      bucket: cfg.bucket,
    };
  }, [range]);

  const query = useTokenUsage({ from, to, bucket });

  return (
    <Page
      title="Token usage"
      description="How many AI tokens we've burned, broken down by repo, conversation, user, task, and model."
    >
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {(Object.keys(RANGES) as RangeKey[]).map((key) => (
          <button
            key={key}
            onClick={() => setRange(key)}
            className={`rounded-md px-2.5 py-1 text-sm transition ${
              range === key
                ? "bg-zinc-900 text-white"
                : "bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50"
            }`}
          >
            {RANGES[key].label}
          </button>
        ))}
      </div>

      {query.isLoading && (
        <p className="text-sm text-zinc-500">Loading usage…</p>
      )}
      {query.isError && (
        <Card title="Error">
          <p className="text-sm text-red-700">
            {query.error instanceof Error
              ? query.error.message
              : "Failed to load usage."}
          </p>
        </Card>
      )}

      {query.data && <UsageView data={query.data} />}
    </Page>
  );
}

function UsageView({ data }: { data: TokenUsageResponse }) {
  const { totals, buckets, byRepo, byConversation, byUser, byTask, byModel } =
    data;

  return (
    <>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Total tokens" value={fmt(totals.totalTokens)} />
        <Stat label="Input" value={fmt(totals.inputTokens)} />
        <Stat label="Output" value={fmt(totals.outputTokens)} />
        <Stat label="Cost (USD)" value={`$${totals.costUsd.toFixed(4)}`} />
      </div>

      <div className="mt-6">
        <Card title={`History (${data.range.bucket})`}>
          {buckets.length === 0 ? (
            <p className="text-sm text-zinc-500">
              No usage recorded in this range yet.
            </p>
          ) : (
            <Histogram buckets={buckets} bucketSize={data.range.bucket} />
          )}
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <BreakdownTable
          title="By repository"
          rows={byRepo.map((r) => ({
            key: r.repoId ?? "unattributed",
            label: r.repoName ?? "(unattributed)",
            totalTokens: r.totalTokens,
            costUsd: r.costUsd,
            requests: r.requests,
          }))}
          emptyHint="Repo ingestion hasn't logged any AI tokens in this range."
        />
        <BreakdownTable
          title="By conversation"
          rows={byConversation.map((r) => ({
            key: r.conversationId ?? "unattributed",
            label: r.conversationTitle ?? "(unattributed)",
            totalTokens: r.totalTokens,
            costUsd: r.costUsd,
            requests: r.requests,
          }))}
          emptyHint="No questions answered in this range."
        />
        <BreakdownTable
          title="By user"
          rows={byUser.map((r) => ({
            key: r.userId ?? "unattributed",
            label: r.userName ?? r.userEmail ?? "(unattributed)",
            totalTokens: r.totalTokens,
            costUsd: r.costUsd,
            requests: r.requests,
          }))}
          emptyHint="No per-user attribution yet (auth not wired up)."
        />
        <BreakdownTable
          title="By task"
          rows={byTask.map((r) => ({
            key: r.task,
            label: r.task,
            totalTokens: r.totalTokens,
            costUsd: r.costUsd,
            requests: r.requests,
          }))}
        />
        <div className="lg:col-span-2">
          <BreakdownTable
            title="By model"
            rows={byModel.map((r) => ({
              key: `${r.providerKind}/${r.model}`,
              label: `${r.providerKind} · ${r.model}`,
              totalTokens: r.totalTokens,
              costUsd: r.costUsd,
              requests: r.requests,
            }))}
          />
        </div>
      </div>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card title={label}>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
    </Card>
  );
}

interface BreakdownRow {
  key: string;
  label: string;
  totalTokens: number;
  costUsd: number;
  requests: number;
}

function BreakdownTable({
  title,
  rows,
  emptyHint,
}: {
  title: string;
  rows: BreakdownRow[];
  emptyHint?: string;
}) {
  return (
    <Card title={title}>
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500">
          {emptyHint ?? "Nothing to show."}
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-zinc-500">
              <th className="pb-2 font-medium">Name</th>
              <th className="pb-2 pr-2 text-right font-medium">Tokens</th>
              <th className="pb-2 pr-2 text-right font-medium">Cost</th>
              <th className="pb-2 text-right font-medium">Calls</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="py-2 pr-3 truncate" title={r.label}>
                  {r.label}
                </td>
                <td className="py-2 pr-2 text-right tabular-nums">
                  {fmt(r.totalTokens)}
                </td>
                <td className="py-2 pr-2 text-right tabular-nums text-zinc-600">
                  ${r.costUsd.toFixed(4)}
                </td>
                <td className="py-2 text-right tabular-nums text-zinc-600">
                  {r.requests}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function Histogram({
  buckets,
  bucketSize,
}: {
  buckets: TokenUsageBucket[];
  bucketSize: TokenUsageBucketSize;
}) {
  const max = Math.max(...buckets.map((b) => b.totalTokens), 1);
  const width = 720;
  const height = 180;
  const padding = { top: 10, right: 8, bottom: 24, left: 48 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const barGap = 2;
  const barW = Math.max(2, innerW / buckets.length - barGap);

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full max-w-full"
        role="img"
        aria-label="Token usage histogram"
      >
        {/* y-axis tick + label */}
        <text
          x={padding.left - 6}
          y={padding.top + 8}
          fontSize={10}
          textAnchor="end"
          fill="#71717a"
        >
          {fmt(max)}
        </text>
        <text
          x={padding.left - 6}
          y={padding.top + innerH}
          fontSize={10}
          textAnchor="end"
          fill="#71717a"
        >
          0
        </text>
        <line
          x1={padding.left}
          x2={padding.left + innerW}
          y1={padding.top + innerH}
          y2={padding.top + innerH}
          stroke="#e4e4e7"
        />
        {buckets.map((b, i) => {
          const h = (b.totalTokens / max) * innerH;
          const x = padding.left + i * (barW + barGap);
          const y = padding.top + innerH - h;
          return (
            <g key={b.bucket}>
              <rect
                x={x}
                y={y}
                width={barW}
                height={h}
                fill="#18181b"
                rx={1}
              >
                <title>
                  {fmtBucketLabel(b.bucket, bucketSize)} · {fmt(b.totalTokens)}{" "}
                  tokens · ${b.costUsd.toFixed(4)}
                </title>
              </rect>
            </g>
          );
        })}
        {/* x-axis: first and last labels */}
        {buckets.length > 0 && (
          <>
            <text
              x={padding.left}
              y={height - 6}
              fontSize={10}
              fill="#71717a"
            >
              {fmtBucketLabel(buckets[0]!.bucket, bucketSize)}
            </text>
            <text
              x={padding.left + innerW}
              y={height - 6}
              fontSize={10}
              textAnchor="end"
              fill="#71717a"
            >
              {fmtBucketLabel(
                buckets[buckets.length - 1]!.bucket,
                bucketSize,
              )}
            </text>
          </>
        )}
      </svg>
    </div>
  );
}

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

function fmtBucketLabel(
  iso: string,
  bucketSize: TokenUsageBucketSize,
): string {
  const d = new Date(iso);
  if (bucketSize === "hour") {
    return `${d.toLocaleDateString(undefined, { month: "short", day: "numeric" })} ${d.getHours().toString().padStart(2, "0")}:00`;
  }
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
